import React from 'react';
import {
  ClientTypeIcon,
  EditIconButton,
  GlobeIcon,
  IndustryTypeIcon,
  OfficialDealerIcon,
  VerifiedIcon,
} from 'components/common/icons';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import { BrandDetailsEdit } from 'pages/admin/brands/brand/details/edit';
import styles from 'pages/admin/clients/client/details/styles.module.scss';
import { Button, Col, IconWrapper, Image, MultiLineEllipsisText, Row, Tag, Typography } from 'components';
import { useDetails } from './useDetails';
import { Link } from 'react-router-dom';

export const Details = React.memo(() => {
  const { isOpen, onModalClose, onModalOpen, brandDetails } = useDetails();
  return (
    <>
      <Row gutter={[0, 16]}>
        <Col span={24} className={styles.main_info}>
          <Row justify={'space-between'} wrap={false}>
            <Col span={20}>
              <Row gutter={[0, 4]}>
                {brandDetails.files?.LOGO && (
                  <Col className={styles.image_wrapper}>
                    <Image src={brandDetails.files.LOGO} preview={false} alt={'Logo'} />
                  </Col>
                )}
                <Col span={24} className={styles.id_container}>
                  <Typography.Text className={'font-size-12'} style={{ color: '#525866' }}>
                    ID: {brandDetails.uuid}
                  </Typography.Text>
                </Col>
                <Col span={24} className={styles.legal_name}>
                  <Typography.Text title={brandDetails.name} className={styles.name}>
                    {brandDetails.name}
                    {brandDetails.verified && <VerifiedIcon />}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col>
              <HasAccess role={PERMISSIONS.brands.brand.write}>
                <Button type={'link'} icon={<EditIconButton />} onClick={onModalOpen} />
              </HasAccess>
            </Col>
          </Row>
          <Row gutter={[0, 4]} className={'margin-t-4'}>
            <Col span={24}>
              <Row style={{ gap: '9px' }}>
                <Typography.Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {brandDetails.formattedCreatedDate}
                </Typography.Text>
                <Typography.Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {brandDetails.formattedLastModifiedDate}
                </Typography.Text>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.additional_info}>
          <Row gutter={[0, 14]}>
            {brandDetails.webSiteUrl && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <GlobeIcon />
                  <Button
                    style={{ padding: 0 }}
                    type="link"
                    href={
                      brandDetails.webSiteUrl?.startsWith('http')
                        ? brandDetails.webSiteUrl
                        : `https://${brandDetails.webSiteUrl}`
                    }
                    target="_blank"
                  >
                    <Typography.Text
                      title={brandDetails.webSiteUrl}
                      type={'secondary'}
                      className={styles.cut_long_text}
                    >
                      {brandDetails.webSiteUrl}
                    </Typography.Text>
                  </Button>
                </Row>
              </Col>
            )}
            {brandDetails.country?.name && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  {brandDetails.country.flagLocation && <IconWrapper src={brandDetails.country.flagLocation} />}
                  <Typography.Text type={'secondary'} className={'font-size-14'}>
                    {brandDetails.country.name}
                  </Typography.Text>
                </Row>
              </Col>
            )}
            {brandDetails.industry?.name && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <IndustryTypeIcon />
                  <Typography.Text type={'secondary'}>{brandDetails.industry?.name}</Typography.Text>
                </Row>
              </Col>
            )}
            {brandDetails.manufacturerWebSiteUrl && (
              <Col span={24}>
                <Row align={'middle'} justify={'space-between'} wrap={false} gutter={4}>
                  <Col>
                    <Row align={'middle'} className={'gap-4'} wrap={false}>
                      <Col>
                        <ClientTypeIcon />
                      </Col>
                      <Col>
                        <Typography.Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                          Manufacturer:
                        </Typography.Text>
                      </Col>
                    </Row>
                  </Col>
                  <Col flex="0 1 auto">
                    <Link
                      to={
                        brandDetails.manufacturerWebSiteUrl?.startsWith('http')
                          ? brandDetails.manufacturerWebSiteUrl
                          : `https://${brandDetails.manufacturerWebSiteUrl}`
                      }
                      target={'_blank'}
                    >
                      <Typography.Text
                        underline
                        type={'secondary'}
                        style={{ whiteSpace: 'nowrap' }}
                        ellipsis={{ tooltip: true }}
                      >
                        {brandDetails.manufacturerWebSiteUrl}
                      </Typography.Text>
                    </Link>
                  </Col>
                </Row>
              </Col>
            )}
            {brandDetails.officialDealer && (
              <Col span={24}>
                <Row align={'middle'} className={styles.additional_info_item}>
                  <OfficialDealerIcon />
                  <Typography.Text type={'secondary'} className={'font-size-14'}>
                    Official Dealer
                  </Typography.Text>
                </Row>
              </Col>
            )}
            {/*TODO system generated tags*/}
          </Row>
        </Col>
        <Col span={24} className={styles.manual_tags}>
          <Row className={styles.tag_container}>
            {brandDetails.manualTags?.map((_tag: string, index: number) => (
              <Tag title={_tag} key={index + _tag} className={'custom-tag'}>
                {_tag}
              </Tag>
            ))}
          </Row>
        </Col>
        <Col span={24}>
          <MultiLineEllipsisText subTitle={'Additional Notes:'} text={brandDetails.additionalNotes} />
        </Col>
      </Row>
      <BrandDetailsEdit open={isOpen} onCancel={onModalClose} />
    </>
  );
});
